import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { donorsService, donationsService } from '../services/api';
import DonationForm from './DonationForm';
import DonorForm from './DonorForm';
import NoteForm from './NoteForm';
import AuditHistory from './AuditHistory';
import AttachmentsTab from './Attachments';
import './DonorDetail.css';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const DonorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [donor, setDonor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [editingDonation, setEditingDonation] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [attachmentCount, setAttachmentCount] = useState(0);
  const { isAdmin, accessToken } = useAuth();

  const fetchDonor = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await donorsService.get(id);
      setDonor(response.data);
      
      // Fetch attachment count
      try {
        const attachmentResponse = await axios.get(
          `/api/attachments?entityType=donor&entityId=${id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );
        setAttachmentCount(attachmentResponse.data.data?.length || 0);
      } catch (attachmentErr) {
        console.error('Error fetching attachments:', attachmentErr);
        setAttachmentCount(0);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch donor details');
      console.error('Error fetching donor:', err);
    } finally {
      setLoading(false);
    }
  }, [id, accessToken]);

  useEffect(() => {
    fetchDonor();
  }, [fetchDonor]);

  const handleDeleteDonation = async (donationId) => {
    if (window.confirm('Delete this donation?')) {
      try {
        await donationsService.delete(donationId);
        setDonor(prev => ({
          ...prev,
          donations: prev.donations.filter(d => d.id !== donationId)
        }));
        alert('Donation deleted');
      } catch (err) {
        alert('Failed to delete donation: ' + err.message);
      }
    }
  };

  const handleEditDonation = (donation) => {
    setEditingDonation(donation);
    setShowDonationForm(false);
  }

  const handleDonationSubmit = () => {
    setShowDonationForm(false);
    setEditingDonation(null);
    fetchDonor(); // Refresh to get updated data
  };

  const handleDeleteDonor = async () => {
    if (window.confirm('Are you sure you want to delete this donor?')) {
      try {
        await donorsService.delete(id);
        alert('Donor deleted successfully');
        navigate('/donors');
      } catch (err) {
        alert('Failed to delete donor: ' + err.message);
      }
    }
  };

  const handleEditSubmit = () => {
    setShowEditForm(false);
    fetchDonor(); // Refresh to get updated data
  };

  if (loading) return <div className="loading">Loading donor details...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!donor) return <div className="alert alert-error">Donor not found</div>;

  const totalDonations = donor.donations?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0;

  return (
    <div className="donor-detail-container">
      <div className="detail-header">
        <button className="btn btn-outline" onClick={() => navigate('/donors')}>
          ← Back to Donors
        </button>
        <h1>{donor.first_name} {donor.last_name}</h1>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowEditForm(true)}
          >
            Edit Donor
          </button>
          {isAdmin && (
            <button className="btn btn-danger" onClick={handleDeleteDonor}>
              Delete Donor
            </button>)}
        </div>
      </div>

      {showEditForm && (
        <DonorForm 
          initialData={donor}
          onSubmit={handleEditSubmit}
          onCancel={() => setShowEditForm(false)}
        />
      )}

      <div className="detail-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'donations' ? 'active' : ''}`}
          onClick={() => setActiveTab('donations')}
        >
          Donations ({donor.donations?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes ({donor.notes?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'attachments' ? 'active' : ''}`}
          onClick={() => setActiveTab('attachments')}
        >
          Attachments ({attachmentCount})
        </button>
        {isAdmin && (
          <button 
            className={`tab ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            Audit History
          </button>
        )}
      </div>

      {activeTab === 'overview' && (
        <div className="detail-section">
          <div className="info-grid">
            <div className="info-card">
              <label>Email</label>
              <p>{donor.email || 'Not provided'}</p>
            </div>

            {donor.phones && donor.phones.length > 0 && (
              <div className="info-card">
                <label>Phone</label>
                <p>
                  {donor.phones.map((phone, index) => (
                    <div key={index}>
                      {phone.phone_number} ({phone.phone_type})
                    </div>
                  ))}
                </p>
              </div>
            )}

            {donor.addresses && donor.addresses.length > 0 && (
              <div className="info-card">
                <label>Address</label>
                <p>
                  {donor.addresses.map((address, index) => (
                    <div key={index}>
                      {address.street_address && <div>{address.street_address}</div>}
                      {(address.city || address.state) && (
                        <div>{address.city}, {address.state} {address.zip_code}</div>
                      )}
                      {address.country && <div>{address.country}</div>}
                    </div>
                  ))}
                </p>
              </div>
            )}

            <div className="info-card">
              <label>Total Donated</label>
              <p className="amount">${totalDonations?.toFixed(2)}</p>
            </div>

            <div className="info-card">
              <label>Member Since</label>
              <p>{new Date(donor.created_at).toLocaleDateString()}</p>
            </div>

            <div className="info-card">
              <label>Last Updated</label>
              <p>{new Date(donor.updated_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'donations' && (
        <div className="detail-section">
          <div className="section-header">
            <h2>Donation History</h2>
            <button 
              className="btn btn-primary"
              onClick={() => setShowDonationForm(true)}
            >
              + Record Donation
            </button>
          </div>

          {showDonationForm && (
            <DonationForm 
              donor_id={parseInt(id)}
              onSubmit={handleDonationSubmit}
              onCancel={() => setShowDonationForm(false)}
            />
          )}

          {editingDonation && (
            <DonationForm 
              donor_id={parseInt(id)}
              initialData={editingDonation}
              onSubmit={handleDonationSubmit}
              onCancel={() => setEditingDonation(null)}
            />
          )}

          {donor.donations && donor.donations.length > 0 ? (
            <div className="table-container">
              <table className="donations-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {donor.donations.map(donation => (
                    <tr key={donation.id}>
                      <td>{new Date(donation.donation_date).toLocaleDateString()}</td>
                      <td className="amount">${donation.amount?.toFixed(2)}</td>
                      <td>{donation.donation_type || '—'}</td>
                      <td className="notes-cell" title={donation.notes}>
                        {donation.notes ? donation.notes.substring(0, 50) : '—'}
                      </td>
                      <td>
                        <button
                          className='btn btn-sm btn-warning'
                          onClick={() => handleEditDonation(donation)}>
                          Edit
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteDonation(donation.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No donations recorded yet. Record the first donation!</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="detail-section">
          <NoteForm 
            entityId={parseInt(id)} 
            entityType="donor"
            onNoteAdded={fetchDonor}
          />
        </div>
      )}

      {activeTab === 'attachments' && (
        <AttachmentsTab 
          entityType="donor" 
          entityId={parseInt(id)}
          token={accessToken}
        />
      )}

      {activeTab === 'audit' && (
        <AuditHistory entityId={id} entityType="donor" />
      )}
    </div>
  );
};

export default DonorDetail;
